import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, AlertCircle, Camera, Loader2, Compass } from 'lucide-react';
import { authApi } from '@/api/client';
import { AUTH_ME_QUERY_KEY, useAuth } from '@/lib/auth';
import { SettingsAppearanceSection } from '@/components/settings/settings-appearance-section';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/** Client-side avatar guard, mirrored server-side (POST /auth/me/avatar). */
const AVATAR_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';
const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

function initialsOf(name: string | undefined): string {
  if (!name) return 'U';
  return (
    name
      .split(' ')
      .map((part) => part[0])
      .filter(Boolean)
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U'
  );
}

/**
 * Général — the first panel of the unified SettingsModal (claude.ai parity).
 *
 * Profil: avatar (uploadable → S3, with preview + initials fallback) + full name
 * (PATCH /auth/me). Then Apparence (`SettingsAppearanceSection`): langue et
 * thème, les deux seuls réglages d'affichage. Le thème n'est pas en lecture
 * seule — `os.css` porte un thème sombre complet et Apparence offre Clair /
 * Sombre / Système (docs/dark-theme-scope.md §5).
 *
 * What is deliberately NOT here — and this list is a product decision, not an
 * oversight (Notion « Last push before going live », Settings):
 *   • le pont « Instructions pour l'agent » vers Personnaliser,
 *   • le contexte système (rôle / organisation / sujets) et la description du
 *     travail — Personnaliser en reste le seul propriétaire,
 *   • la ligne Police, en lecture seule : la DA ne livre qu'une fonte, donc le
 *     réglage ne réglait rien,
 *   • le titre « Préférences », qui ne coiffait plus qu'un seul bloc.
 */
export interface SettingsGeneralSectionProps {
  /** Re-run the first-run intro flow ("Revoir l'introduction"). */
  onReplayOnboarding?: () => void;
}

export function SettingsGeneralSection({
  onReplayOnboarding,
}: SettingsGeneralSectionProps = {}) {
  const { t } = useTranslation('os');
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [name, setName] = useState(user?.name ?? '');
  const [saveState, setSaveState] = useState<SaveState>('idle');

  const trimmed = name.trim();
  const dirty = trimmed.length > 0 && trimmed !== (user?.name ?? '');

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!dirty) return;
    setSaveState('saving');
    try {
      await authApi.updateProfile({ name: trimmed });
      await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 3000);
    } catch {
      setSaveState('error');
    }
  };

  // ── Avatar upload ────────────────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const avatarMutation = useMutation({
    mutationFn: (file: File) => authApi.uploadAvatar(file),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
      // The refreshed profile now carries the new avatarUrl; drop the local preview.
      if (preview) URL.revokeObjectURL(preview);
      setPreview(null);
    },
    onError: () => {
      if (preview) URL.revokeObjectURL(preview);
      setPreview(null);
      setAvatarError(t('settings.general.avatar.error'));
    },
  });

  const onAvatarChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setAvatarError(null);
    if (!AVATAR_ACCEPT.split(',').includes(file.type)) {
      setAvatarError(t('settings.general.avatar.typeError'));
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setAvatarError(t('settings.general.avatar.sizeError'));
      return;
    }
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(file));
    avatarMutation.mutate(file);
  };

  const avatarSrc = preview ?? user?.avatarUrl ?? null;

  return (
    <div className="space-y-6">
      {/* ── Profil ─────────────────────────────────────────────────── */}
      <section className="space-y-4" data-testid="settings-general-profile">
        <h3 className="font-heading text-[0.95rem] font-semibold text-foreground">
          {t('settings.general.profileTitle')}
        </h3>

        <div className="flex items-center gap-3">
          {/* Avatar with upload trigger. Square brand DA (rounded-control); the
              overlaid camera button opens the hidden file input. */}
          <div className="relative shrink-0">
            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-control border border-signal/30 bg-signal/15 font-ui text-[1.05rem] font-semibold text-signal-ink">
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt={t('settings.general.avatar.alt')}
                  data-testid="settings-general-avatar-img"
                  className="h-full w-full object-cover"
                />
              ) : (
                <span data-testid="settings-general-avatar-initials">{initialsOf(user?.name)}</span>
              )}
              {avatarMutation.isPending && (
                <span
                  className="absolute inset-0 flex items-center justify-center bg-scrim/40"
                  data-testid="settings-general-avatar-uploading"
                  role="status"
                  aria-label={t('settings.general.avatar.uploading')}
                >
                  <Loader2 className="h-4 w-4 animate-spin text-background" aria-hidden />
                </span>
              )}
            </div>
            <button
              type="button"
              data-testid="settings-general-avatar-button"
              onClick={() => fileInputRef.current?.click()}
              disabled={avatarMutation.isPending}
              aria-label={t('settings.general.avatar.change')}
              className="absolute -bottom-1.5 -right-1.5 flex h-6 w-6 items-center justify-center rounded-control border border-border bg-card text-ink-2 shadow-sm transition-colors hover:bg-secondary hover:text-foreground disabled:cursor-default disabled:opacity-50"
            >
              <Camera className="h-3.5 w-3.5" strokeWidth={1.8} aria-hidden />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={AVATAR_ACCEPT}
              data-testid="settings-general-avatar-input"
              onChange={onAvatarChange}
              className="hidden"
            />
          </div>

          <div className="min-w-0">
            <p className="truncate text-[0.9rem] font-medium text-foreground">{user?.name || '—'}</p>
            <p className="truncate font-data text-[10px] lowercase tracking-wide text-ink-2">
              {user?.email || ''}
            </p>
            <p className="mt-0.5 text-[0.7rem] text-ink-2">{t('settings.general.avatar.hint')}</p>
          </div>
        </div>
        {avatarError && (
          <p
            role="alert"
            data-testid="settings-general-avatar-error"
            className="flex items-center gap-1 text-[0.78rem] text-destructive"
          >
            <AlertCircle className="h-3.5 w-3.5" aria-hidden /> {avatarError}
          </p>
        )}

        <form onSubmit={handleSave} className="space-y-2">
          <label htmlFor="settings-full-name" className="block">
            <span className="mb-1 block font-data text-[9px] uppercase tracking-[0.2em] text-ink-2">
              {t('settings.general.fullName')}
            </span>
            <input
              id="settings-full-name"
              data-testid="settings-general-name"
              value={name}
              autoComplete="name"
              onChange={(e) => {
                setName(e.target.value);
                if (saveState !== 'idle') setSaveState('idle');
              }}
              className="w-full rounded-control border border-border bg-surface-2 px-2.5 py-1.5 text-[0.85rem] text-foreground placeholder:text-ink-2 outline-none focus:border-signal/60"
            />
          </label>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              data-testid="settings-general-name-save"
              disabled={!dirty || saveState === 'saving'}
              className="inline-flex items-center gap-1 rounded-control bg-primary px-3 py-1.5 font-ui text-[0.8rem] font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-default disabled:opacity-40"
            >
              {saveState === 'saving' ? t('settings.general.saving') : t('settings.general.save')}
            </button>
            {saveState === 'saved' && (
              <span
                role="status"
                className="inline-flex items-center gap-1 font-data text-[9px] uppercase tracking-wider text-signal-ink"
              >
                <Check className="h-3 w-3" strokeWidth={2} aria-hidden /> {t('settings.general.saved')}
              </span>
            )}
            {saveState === 'error' && (
              <span
                role="alert"
                className="inline-flex items-center gap-1 text-[0.78rem] text-destructive"
              >
                <AlertCircle className="h-3.5 w-3.5" aria-hidden /> {t('settings.general.saveError')}
              </span>
            )}
          </div>
        </form>

        {/* Replay the first-run intro on demand. Non-destructive: it re-opens the
            flow without resetting the onboarded flag. Rendered only when the shell
            wired a handler (absent in standalone/legacy mounts). */}
        {onReplayOnboarding && (
          <div className="border-t border-border pt-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[0.85rem] font-medium text-foreground">
                  {t('settings.general.onboarding.title')}
                </p>
                <p className="text-[0.75rem] text-ink-2">{t('settings.general.onboarding.hint')}</p>
              </div>
              <button
                type="button"
                data-testid="settings-general-replay-onboarding"
                onClick={onReplayOnboarding}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-control border border-border bg-surface-2 px-3 py-1.5 font-ui text-[0.8rem] font-medium text-foreground transition-colors hover:bg-secondary"
              >
                <Compass className="h-3.5 w-3.5" strokeWidth={1.8} aria-hidden />
                {t('settings.general.onboarding.button')}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ── Apparence ──────────────────────────────────────────────── */}
      {/* Untitled on purpose: the "Préférences" heading coiffait un seul bloc,
          et ce bloc porte déjà son propre titre. */}
      <section className="space-y-3 border-t border-border pt-5" data-testid="settings-general-preferences">
        <SettingsAppearanceSection />
      </section>
    </div>
  );
}
